import { useParams, useNavigate } from 'react-router-dom';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import DestinationPartnerIcon from '../../components/icons/DestinationPartnerIcon';
import { useQuery } from '@apollo/client';
import { DESTINATION_PARTNER_BY_SLUG_QUERY } from '../../queries/destinationQueries';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';

export default function DestinationConnect() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { loading, error, data } = useQuery(DESTINATION_PARTNER_BY_SLUG_QUERY, {
    variables: { slug },
    fetchPolicy: 'cache-and-network',
  });

  const name = data?.destinationPartnerBySlug?.name;

  if (loading) {
    return (
      <LoadingSpinner
        message="Getting Destination Partner Connection Details..."
        className="mt-10"
      />
    );
  }

  if (error) {
    return (
      <ErrorText message="We were unable to retrieve Partner's Connection Details. Please try again later." />
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-40px)] bg-gray-50 relative">
      <div className="bg-white p-6 border-2 border-gray-200 rounded-lg shadow-sm w-full max-w-xl absolute top-[calc(50%-60px)] left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="flex items-center space-x-3">
          <DestinationPartnerIcon slug={slug} size={32} />
          <h2 className="text-xl font-semibold p-0 m-0">Connect to {name}</h2>
        </div>

        <div className="mt-5 mb-8 p-4 pb-6 bg-gray-100 rounded-lg text-sm">
          <p className="font-medium mb-4">Before you connect:</p>
          <ul className="list-disc list-inside space-y-3 text-gray-600">
            <li>Make sure you have admin access to your {name} account</li>
            <li>You'll be redirected to {name} to authorize the connection</li>
            <li>You'll need to select which ad accounts to connect</li>
          </ul>
        </div>

        <div className="mt-4">
          <p className="font-medium">We'll request permission to:</p>
          <ul className="mt-2 space-y-3 text-sm text-gray-700">
            <li className="flex items-center space-x-2">
              <IoIosCheckmarkCircleOutline className="text-2xl text-green-light" />{' '}
              <span>Access your ad accounts</span>
            </li>
            <li className="flex items-center space-x-2">
              <IoIosCheckmarkCircleOutline className="text-2xl text-green-light" />
              <span>Create and manage custom audiences</span>
            </li>
            <li className="flex items-center space-x-2">
              <IoIosCheckmarkCircleOutline className="text-2xl text-green-light" />{' '}
              <span>View ads performance data</span>
            </li>
          </ul>
        </div>

        <div className="mt-6 flex justify-end space-x-2">
          <ConnectButton variant="white" handleClick={() => navigate(-1)}>
            Cancel
          </ConnectButton>
          <ConnectButton variant="blue" handleClick={() => navigate(-1)}>
            Continue to {name}
          </ConnectButton>
        </div>
      </div>
    </div>
  );
}

function ConnectButton({
  variant = 'blue',
  handleClick,
  children,
}: {
  variant?: 'blue' | 'white';
  handleClick?: () => void;
  children: React.ReactNode;
}) {
  const baseStyles = 'px-5 py-2 rounded-lg transition';
  const blueStyles = 'bg-blue-light text-white hover:bg-blue';
  const whiteStyles =
    'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100';

  return (
    <button
      onClick={() => handleClick()}
      className={`${baseStyles} ${
        variant === 'blue' ? blueStyles : whiteStyles
      }`}
    >
      {children}
    </button>
  );
}
